import * as THREE from "three";

import { Engine } from "./engine";
import { Ship } from "./ship";
import { RingField } from "./rings";
import { Nebula } from "./nebula";
import { Particles } from "./particles";
import { StreakAura } from "./streak-aura";
import { BoostRing } from "./boost-ring";
import { VaporField } from "./vapor";
import { PowerUps } from "./power-ups";
import { SecretRings } from "./secret-rings";
import { GhostShip } from "./ghost-ship";
import { Input } from "./input";
import { AudioBed } from "./audio";
import { startRun, RingEventOut } from "./api";
import { biomeFor, BIOMES } from "./biomes";
import { effectivePalette, TrailPalette } from "../ui/trail-variants";

export type GameState = "idle" | "running" | "paused" | "over";

export interface GameCallbacks {
  onStateChange: (state: GameState) => void;
  onHud: (score: number, time: number, mult: number) => void;
  onRunEnd: (summary: RunResult) => void;
  /** Engine signals sustained slow frames. Caller usually downgrades quality. */
  onPerfPressure?: (avgFrameMs: number) => void;
  /** Pass/miss event with run snapshot. Consumers: achievement system,
   *  gate-ring flash effect, boss-ring fanfare, miss-heatmap.
   *  `gate` = every 13th, `boss` = every 50th (boss supersedes gate when
   *  both would fire). `missLateralSigned` = ring.x - ship.x at miss time. */
  onRingEvent?: (e: {
    type: "pass" | "miss";
    ringIdx: number;
    score: number;
    streak: number;
    noMissSeconds: number;
    gate: boolean;
    boss: boolean;
    missLateralSigned: number;
  }) => void;
}

export interface RunResult {
  runId: string | null;
  score: number;          // client-estimated (server is authoritative)
  events: RingEventOut[];
  durationS: number;
  /** Per-run stats — surfaced on the run-end overlay summary card. */
  stats: {
    passes: number;
    misses: number;
    bestStreak: number;
    biomeIdxs: number[];
  };
}

// Must mirror functions/_lib/scoring.ts (which mirrors
// docs/reference-implementation/app/scoring.py — canonical).
const TIME_START = 30;
const TIME_PER_PASS = 1.6;
const TIME_PER_MISS = -3.0;
const PASS_SCORE_BASE = 100;
const MAX_MULT = 3.0;
/** Mirrors functions/_lib/scoring.ts ACT_THRESHOLDS. Local copy keeps
 *  game.ts free of a /functions/_lib import (Pages Functions module
 *  graph stays disjoint from the Vite bundle). */
const ACT_THRESHOLDS_CLIENT = [0, 50, 100] as const;
function actForRingIdxClient(idx: number): number {
  for (let i = ACT_THRESHOLDS_CLIENT.length - 1; i >= 0; i--) {
    if (idx >= ACT_THRESHOLDS_CLIENT[i]) return i;
  }
  return 0;
}
/** Per-act sustained-chime target: Open is rest (chime silent), River
 *  is busy (mid presence), Chase is intense (near full). Ramps via the
 *  AudioBed setChimeGain smoothing param — additions-only audio surface. */
const ACT_CHIME_TARGET = [0.0, 0.4, 0.85] as const;
const ACT_CHIME_RAMP_MS = 1800;

export class Game {
  private engine: Engine;
  private ship = new Ship();
  private rings = new RingField();
  private nebula = new Nebula();
  private particles = new Particles();
  private streakAura = new StreakAura();
  private boostRing = new BoostRing();
  private vapor = new VaporField();
  private powerUps = new PowerUps();
  private secretRings = new SecretRings();
  private ghost = new GhostShip();
  private ghostEnabled = false;
  private secretRingsFound = 0;
  // Power-up buff timers (engine-clock seconds when buff expires).
  private freezeUntil = 0;
  private multBoostUntil = 0;
  private input: Input;
  private audio = new AudioBed();

  private state: GameState = "idle";
  private score = 0;
  private timeLeft = TIME_START;
  private streak = 0;
  private bestStreakThisRun = 0;
  private passesThisRun = 0;
  private missesThisRun = 0;
  private biomesThisRun = new Set<number>();
  private mult = 1;
  private runs = 0;
  private runStartedAt = 0;
  private runId: string | null = null;
  private events: RingEventOut[] = [];

  private cb: GameCallbacks;
  private camPos = new THREE.Vector3(0, 1.4, 6);
  private camLook = new THREE.Vector3();
  // Hero entrance: a 4-second slow dolly from a higher/further pose to the
  // resting hero pose. Suppressed for users who navigate to the page mid-run
  // (state !== 'idle') or who have reduced-motion enabled.
  private entranceStart = -1;
  private static readonly ENTRANCE_S = 4.0;
  private joystick = new THREE.Vector2();

  // Camera trauma model (GMTK-style): events add trauma in 0..1; each frame
  // the value decays at 1.5/sec; rotation perturbations are proportional to
  // trauma². Suppressed entirely under reduced-motion.
  private trauma = 0;
  // Tune notes: at 1.5/sec decay a 0.6 miss-impulse lost meaningful shake
  // (trauma² ≥ ~0.1) inside 200ms — read as a flicker. 1.1/sec extends the
  // perceptible window to ~330ms, closer to a thump. MISS_ADD bumped 0.6→0.7
  // so a single miss reads as visceral (0.49 peak shake) without saturating
  // — two misses still cap to 1.0.
  private static readonly TRAUMA_DECAY = 1.1;
  private static readonly TRAUMA_MISS_ADD = 0.7;
  // Lateral distance under which a miss reads as a "near miss" (ring scraped
  // past) — smaller trauma + smaller burst so the player feels the scrape
  // distinct from a clean whiff.
  private static readonly NEAR_MISS_LATERAL = 1.6;
  private static readonly TRAUMA_NEAR_MISS_ADD = 0.25;

  // Streak ribbon trail — color reused across frames to avoid per-frame
  // Color allocation. Hue shifts along the active trail palette's
  // hueStart→hueEnd as the streak climbs. Palette is set from main.ts
  // (settings drawer) via setTrailPalette(); legacy "ribbon" is the
  // always-unlocked Novice default.
  private trailColor = new THREE.Color();
  private trailPalette: TrailPalette = effectivePalette("ribbon");

  // Practice mode: endless run, no time burn, no server submission.
  // Toggled via settings drawer; main.ts mirrors the value here.
  private practice = false;
  // Endless mode: no time burn (like practice) but DOES submit to the
  // 'drift-endless' leaderboard scope. Player ends the run via the
  // End-run button (main.ts) which calls endRun(). Daily/speedrun take
  // priority; daily seed + endless together would be incoherent (daily
  // is a fixed-length comparison).
  private endless = false;

  // Last-miss timestamp (engine-clock seconds) — drives the no-miss-minute
  // achievement check. Reset to runStartedAt on each run.
  private lastMissAt = 0;
  // Climax window — engine-clock seconds during which the cinematic stack
  // is saturated regardless of ship.speedBoost. Fired once per run when
  // streak first crosses 30.
  private climaxUntil = 0;
  private climaxFiredThisRun = false;
  // Highest act reached so far this run. Drives the act-specific
  // sustained-chime bed: promoted to River at first ring-50 pass,
  // promoted to Chase at first ring-100 pass. Reset on start().
  private currentAct = 0;

  constructor(canvas: HTMLCanvasElement, cb: GameCallbacks) {
    this.cb = cb;
    this.engine = new Engine(canvas, {
      onFrame: (dt, t) => this.frame(dt, t),
      onPerfPressure: (avgMs) => this.cb.onPerfPressure?.(avgMs),
    });

    this.engine.scene.add(this.nebula.mesh);
    this.engine.scene.add(this.rings.group);
    this.engine.scene.add(this.ship.group);
    this.engine.scene.add(this.particles.points);
    this.engine.scene.add(this.streakAura.mesh);
    this.engine.scene.add(this.boostRing.mesh);
    this.engine.scene.add(this.vapor.points);
    this.engine.scene.add(this.powerUps.group);
    this.engine.scene.add(this.secretRings.group);
    this.engine.scene.add(this.ghost.mesh);

    this.input = new Input(canvas);
    this.engine.start();
  }

  dispose() {
    this.input.dispose();
    this.engine.dispose();
  }

  setPractice(b: boolean) {
    this.practice = b;
  }

  setEndless(b: boolean) {
    this.endless = b;
  }

  get isEndless(): boolean {
    return this.endless;
  }

  /** Player-triggered end (Pause overlay "End run" button). Endless mode
   *  has no time-out path, so this is the canonical exit. Idempotent. */
  endRun() {
    if (this.state === "running" || this.state === "paused") {
      this.end();
    }
  }

  setGhostEnabled(b: boolean) {
    this.ghostEnabled = b;
  }

  /** Speedrun: end the run as soon as score crosses this target. 0 disables. */
  setSpeedrunTarget(target: number) {
    this.speedrunTarget = target;
  }
  private speedrunTarget = 0;

  /**
   * Modifier "spice" — when enabled, game.start() picks a random label
   * from this list and applies the matching behavioral tweak for the
   * upcoming run. The set is deliberately small + survivable.
   */
  private modifiersEnabled = false;
  private modifier: string | null = null;
  setModifiersEnabled(b: boolean) {
    this.modifiersEnabled = b;
  }
  get activeModifier(): string | null {
    return this.modifier;
  }
  private static readonly MODIFIERS = [
    "half_clock",     // start with 15s instead of 30
    "inverted_x",     // lateral input inverted
    "no_boost",       // Space / hold does nothing
    "heavy_trauma",   // misses shake harder
    "all_boss",       // every 13th feels like a boss too
  ] as const;

  /**
   * Apply a cosmetic palette to the ship meshes. Order follows ship.ts
   * construction (hull, wingL, wingR, disc, halo). Pure material color
   * updates — geometry, lights, physics, and lookAt rig are untouched.
   */
  setShipPalette(palette: {
    hullColor: number;
    hullEmissive: number;
    wingEmissive: number;
    discColor: number;
    haloColor: number;
  }) {
    const kids = this.ship.group.children;
    if (kids.length < 5) return;
    const hullMat = (kids[0] as THREE.Mesh).material as THREE.MeshStandardMaterial;
    const wingL = (kids[1] as THREE.Mesh).material as THREE.MeshStandardMaterial;
    const wingR = (kids[2] as THREE.Mesh).material as THREE.MeshStandardMaterial;
    const discMat = (kids[3] as THREE.Mesh).material as THREE.MeshBasicMaterial;
    const haloMat = (kids[4] as THREE.Mesh).material as THREE.MeshBasicMaterial;
    hullMat.color.setHex(palette.hullColor);
    hullMat.emissive.setHex(palette.hullEmissive);
    wingL.emissive.setHex(palette.wingEmissive);
    wingR.emissive.setHex(palette.wingEmissive);
    discMat.color.setHex(palette.discColor);
    haloMat.color.setHex(palette.haloColor);
  }

  /**
   * Select a streak-trail palette by id. main.ts routes the settings
   * drawer's selection here; locked ids transparently fall back to the
   * always-unlocked "ribbon" via effectivePalette().
   */
  setTrailPalette(id: string) {
    this.trailPalette = effectivePalette(id);
  }

  /**
   * Daily challenge: caller passes a deterministic seed (today's date hash)
   * and the run plays locally (no /api/runs). Pass null to clear and use
   * the server-issued seed. Mutually overrides practice's random seed.
   */
  setDailySeed(seed: number | null) {
    this.dailySeed = seed;
  }
  private dailySeed: number | null = null;

  async start(turnstileToken?: string | null) {
    this.audio.arm();
    this.audio.setDroneIntensity(1);

    // Three start paths:
    //   - daily : deterministic local seed, no submit
    //   - practice: random local seed, no submit
    //   - default: server-issued seed + run id (ranked)
    let seed: number;
    if (this.dailySeed !== null) {
      seed = this.dailySeed;
      this.runId = null;
    } else if (this.practice) {
      seed = Math.floor(Math.random() * 0x7fffffff);
      this.runId = null;
    } else {
      // Endless mode goes through the same ranked startRun path, but
      // tags the run as 'drift-endless' so the server tracks the scope.
      const game: "drift" | "drift-endless" = this.endless ? "drift-endless" : "drift";
      const run = await startRun(turnstileToken, game);
      seed = run?.seed ?? Math.floor(Math.random() * 0x7fffffff);
      this.runId = run?.run_id ?? null;
    }

    this.score = 0;
    this.streak = 0;
    this.bestStreakThisRun = 0;
    this.passesThisRun = 0;
    this.missesThisRun = 0;
    this.biomesThisRun.clear();
    this.mult = 1;
    this.timeLeft = TIME_START;
    this.events = [];
    this.runStartedAt = this.engine.clock.elapsedTime;

    this.ship.reset();
    this.rings.reset(seed);
    this.powerUps.reset(seed ^ 0x5a5a5a5a);
    this.secretRings.reset(seed);

    // Pick a fresh modifier for this run when spice is on.
    if (this.modifiersEnabled) {
      const list = Game.MODIFIERS;
      this.modifier = list[Math.floor(Math.random() * list.length)];
      if (this.modifier === "half_clock") this.timeLeft = 15;
    } else {
      this.modifier = null;
    }
    this.secretRingsFound = 0;
    this.ghost.startRecording();
    this.freezeUntil = 0;
    this.multBoostUntil = 0;
    this.climaxUntil = 0;
    this.climaxFiredThisRun = false;
    this.lastMissAt = this.runStartedAt;
    this.currentAct = 0;
    // Open act starts silent on the sustained-chime stem. Snap to 0 so
    // the prior run's Chase-act presence doesn't bleed into the new Open.
    this.audio.setChimeGain(ACT_CHIME_TARGET[0], 0);
    this.setState("running");
  }

  pause() {
    if (this.state !== "running") return;
    this.setState("paused");
    this.audio.setDroneIntensity(0.2);
  }
  resume() {
    if (this.state !== "paused") return;
    this.setState("running");
    this.audio.setDroneIntensity(1);
  }

  private end() {
    this.runs += 1;
    this.setState("over");
    this.audio.setDroneIntensity(0);
    // Unwind the sustained-chime bed in lockstep with the drone so the
    // run-end silence is uniform across stems.
    this.audio.setChimeGain(0, 600);
    this.ghost.stopRecording();
    const durationS = this.engine.clock.elapsedTime - this.runStartedAt;
    this.cb.onRunEnd({
      runId: this.runId,
      score: this.score,
      events: this.events,
      durationS,
      stats: {
        passes: this.passesThisRun,
        misses: this.missesThisRun,
        bestStreak: this.bestStreakThisRun,
        biomeIdxs: Array.from(this.biomesThisRun).sort(),
      },
    });
  }

  private setState(s: GameState) {
    this.state = s;
    this.cb.onStateChange(s);
  }

  get currentState() {
    return this.state;
  }
  get runsThisSession() {
    return this.runs;
  }
  get inputSystem() {
    return this.input;
  }

  /** Debug-HUD-only telemetry. Stable shape for callers; no UI dependency. */
  perfSnapshot(): { avgMs: number; fps: number; boost: number; trauma: number } {
    const avgMs = this.engine.avgFrameMs;
    return {
      avgMs,
      fps: avgMs > 0 ? 1000 / avgMs : 0,
      boost: this.engine.currentBoost,
      trauma: this.trauma,
    };
  }

  applyQualityProfile(p: {
    pixelRatioCap: number;
    bloomEnabled: boolean;
    bloomStrength: number;
    motionEffects?: boolean;
  }) {
    this.engine.setPixelRatio(p.pixelRatioCap);
    this.engine.setBloom(p.bloomEnabled ? p.bloomStrength : 0);
    // Motion effects respect both the quality profile and reduced-motion.
    // Reduced-motion overrides via setReducedMotion(); profile sets the
    // current desired state, reduced-motion can then force it off.
    const motion = p.motionEffects !== false && !this.reducedMotionActive();
    this.engine.setMotionEffectsEnabled(motion);
  }

  setReducedMotion(reduced: boolean) {
    // When the user toggles reduced-motion in settings, immediately apply.
    if (reduced) {
      this.engine.setMotionEffectsEnabled(false);
      this.trauma = 0;
    }
    // When turning OFF, the next applyQualityProfile will re-enable.
  }

  private reducedMotionActive(): boolean {
    return typeof document !== "undefined" &&
      document.body.classList.contains("reduced-motion");
  }

  setMuted(muted: boolean) {
    this.audio.setMuted(muted);
  }

  setAudioVolume(v: number) {
    this.audio.setVolume(v);
  }

  /** Prime the AudioContext. MUST be called synchronously inside a user
   *  gesture (iOS Safari rejects out-of-gesture AudioContext creation). */
  armAudio() {
    this.audio.arm();
  }

  /** Resume the AudioContext after a backgrounding event. iOS Safari
   *  aggressively suspends contexts when the tab goes background or
   *  the screen locks. */
  resumeAudio() {
    this.audio.resume();
  }

  private lastBiomeIdx = -1;
  private biomeBlendA = new THREE.Color();
  private biomeBlendB = new THREE.Color();
  private biomeBlendC = new THREE.Color();
  private biomeFog = new THREE.Color();

  // ---- frame ----
  private frame(dt: number, t: number) {
    this.nebula.update(t, this.ship.position);
    this.updateBiome();

    if (this.state === "running") {
      // Auto-pause after 15s of zero genuine input — prevents a run from
      // burning out when the player walks away mid-flight.
      if (performance.now() - this.input.lastActivityMs > 15000) {
        this.pause();
      }
      this.input.poll(this.joystick);
      // Modifier hooks before ship.update.
      if (this.modifier === "inverted_x") this.joystick.x = -this.joystick.x;
      const boostInput = this.modifier === "no_boost" ? false : this.input.boost;
      this.ship.update(dt, this.joystick, boostInput);
      // Climax window — fired once when streak first hits 30. The cinematic
      // stack is force-saturated to 1.0 for 3 seconds; combined with a
      // particle storm and high chime detune it reads as "the universe folds
      // for a beat." Ship physics untouched; this is purely render + audio.
      // Suppressed under reduced-motion (3s of saturated post-FX is exactly
      // the kind of moment that triggers vestibular reactions).
      if (!this.climaxFiredThisRun && this.streak >= 30 && !this.reducedMotionActive()) {
        this.climaxFiredThisRun = true;
        this.climaxUntil = t + 3.0;
        for (let i = 0; i < 8; i++) {
          const offset = new THREE.Vector3(
            this.ship.position.x,
            this.ship.position.y,
            this.ship.position.z - 4 - i * 6,
          );
          this.particles.burst(offset, new THREE.Color(0xffffff), 32, 9);
          this.particles.burst(offset, new THREE.Color(0x00e0ff), 32, 7);
        }
        // Two chimes (root + octave) for fanfare; spatial centered.
        this.audio.chime(7, 0);
        this.audio.chime(19, 0);
      }
      const climaxActive = t < this.climaxUntil;
      const cinemaBoost = climaxActive ? 1.0 : this.ship.speedBoost;
      // Feed adaptive music + engine cinematic effects with the live speed.
      this.audio.setSpeedIntensity(cinemaBoost);
      this.engine.setBoost(cinemaBoost, dt);

      // Approach intensity = proximity to nearest ring ahead of the ship.
      // Ship moves toward -Z; "ahead" means ring.z < ship.z (more negative).
      // dz > 0 here means the ring is in front; we want intensity to ramp
      // up as dz shrinks toward 0. Cap at 12u out.
      let nearestDz = Infinity;
      for (const m of this.rings.group.children) {
        const dz = this.ship.position.z - (m as THREE.Object3D).position.z;
        if (dz > 0 && dz < nearestDz) nearestDz = dz;
      }
      const approach = nearestDz === Infinity ? 0 : Math.max(0, 1 - nearestDz / 12);
      this.audio.setApproach(approach);

      // Ghost ship recording + playback.
      const runT = t - this.runStartedAt;
      this.ghost.sample(this.ship.position, runT);
      this.ghost.update(runT, this.ghostEnabled);

      // Secret rings (rare gold rings; pure visual fanfare on pass).
      const secrets = this.secretRings.update(dt, this.ship.position);
      for (const s of secrets) {
        this.secretRingsFound += 1;
        this.particles.burst(s.position, new THREE.Color(0xffd54a), 96, 11);
        this.particles.burst(s.position, new THREE.Color(0xffe88c), 64, 8);
        this.audio.chime(19, 0); // octave + 5th over the arp root
      }

      // Power-ups update + pickup events.
      const pickups = this.powerUps.update(dt, this.ship.position);
      for (const p of pickups) {
        if (p.kind === "freeze") {
          this.freezeUntil = t + 3.0;
          this.particles.burst(p.position, new THREE.Color(0x66e0ff), 48, 7);
          this.audio.chime(7, 0); // perfect-fifth chime to mark pickup
        } else {
          this.multBoostUntil = t + 5.0;
          this.particles.burst(p.position, new THREE.Color(0xff66ee), 48, 7);
          this.audio.chime(12, 0); // octave chime
        }
      }

      const events = this.rings.update(dt, t, this.ship.position);
      for (const e of events) {
        const eventT = t - this.runStartedAt;
        // 'all_boss' modifier promotes every gate to boss treatment.
        const baseBoss = e.type === "pass" && e.ringIdx > 0 && e.ringIdx % 50 === 0;
        const baseGate = !baseBoss && e.type === "pass" && e.ringIdx > 0 && e.ringIdx % 13 === 0;
        const isBoss = baseBoss || (this.modifier === "all_boss" && baseGate);
        const isGate = baseGate && !isBoss;
        if (e.type === "pass") {
          this.streak += 1;
          if (this.streak > this.bestStreakThisRun) this.bestStreakThisRun = this.streak;
          this.passesThisRun += 1;
          this.biomesThisRun.add(biomeFor(e.ringIdx).index);
          // Act-promotion → sustained-chime presence shifts. Open is rest,
          // River busy, Chase intense. Ramp slowly (1.8s) so the bed swells
          // rather than snapping. Only fires on the first ring in the new
          // act tier this run, even when act_reached jumps multiple tiers
          // (rare but defensible — e.g., a future big-skip modifier).
          const ringAct = actForRingIdxClient(e.ringIdx);
          if (ringAct > this.currentAct) {
            for (let a = this.currentAct + 1; a <= ringAct; a++) {
              this.audio.setChimeGain(ACT_CHIME_TARGET[a], ACT_CHIME_RAMP_MS);
            }
            this.currentAct = ringAct;
          }
          this.mult = Math.min(MAX_MULT, 1 + this.streak * 0.05);
          // Mult buff doubles the effective multiplier while active (capped by
          // MAX_MULT * 2). Server replay is unaffected — this only shapes the
          // optimistic client estimate. Authoritative score still wins.
          const effectiveMult = t < this.multBoostUntil ? this.mult * 2 : this.mult;
          this.score += Math.round(PASS_SCORE_BASE * effectiveMult);
          this.timeLeft = Math.min(60, this.timeLeft + TIME_PER_PASS);
          // Boss rings (every 50th) get a 5x burst + arpeggio fanfare.
          // Gate rings (every 13th) get a triple-size burst + brighter color.
          if (isBoss) {
            this.particles.burst(e.position, new THREE.Color(0xff00ff), 120, 14);
            this.particles.burst(e.position, new THREE.Color(0xffffff), 96, 12);
            this.particles.burst(e.position, new THREE.Color(0x00e0ff), 64, 10);
          } else if (isGate) {
            this.particles.burst(e.position, new THREE.Color(0xffffff), 72, 12);
            this.particles.burst(e.position, new THREE.Color(0x00e0ff), 48, 9);
          } else {
            this.particles.burst(e.position, new THREE.Color(0x00e0ff), 36, 8);
          }
          // Pan by ring's lateral offset from the ship; ±10u maps to ±1 pan.
          const panX = THREE.MathUtils.clamp((e.position.x - this.ship.position.x) / 10, -1, 1);
          // Audio key-up: detune was capped at 12 (a flatted-5th over the
          // arp root). Past streak 12 we keep climbing one semitone per
          // five-streak, so a streak of 30 rides up another minor third —
          // distinct enough to hear without leaving the harmonic field.
          // Gate rings ride an octave higher (+12 semitones) for fanfare.
          const baseDetune = this.streak <= 12
            ? this.streak
            : 12 + Math.floor((this.streak - 12) / 5);
          // Boss rings ride a 4-note arpeggio (root + 5th + octave + 12th)
          // for fanfare. Gate rings get a single +12 chime. Regular rings
          // use the streak-curve detune.
          if (isBoss) {
            this.audio.chime(0, panX);
            this.audio.chime(7, panX);
            this.audio.chime(12, panX);
            this.audio.chime(19, panX);
          } else {
            const chimeDetune = isGate ? baseDetune + 12 : baseDetune;
            this.audio.chime(chimeDetune, panX);
          }
          // Soft haptic on success (Android Chrome, some Samsung; no-op on iOS).
          navigator.vibrate?.(isBoss ? [20, 30, 20, 30, 20] : (isGate ? [10, 20, 10] : 12));
        } else {
          this.streak = 0;
          this.missesThisRun += 1;
          this.mult = 1;
          this.timeLeft += TIME_PER_MISS;
          // Classify by lateral distance: clean whiff vs. ring-scraped-by.
          // Server still receives the same "passed: false" event — this only
          // shapes the client-side tactile response.
          const lateral = Math.abs(e.position.x - this.ship.position.x);
          const isNearMiss = lateral < Game.NEAR_MISS_LATERAL;
          if (isNearMiss) {
            this.particles.burst(e.position, new THREE.Color(0xff3df0), 12, 3.5);
            this.trauma = Math.min(1, this.trauma + Game.TRAUMA_NEAR_MISS_ADD);
            navigator.vibrate?.(15);
          } else {
            this.particles.burst(e.position, new THREE.Color(0xff3df0), 24, 5);
            this.audio.thud();
            // Camera trauma — sidechain-style screen-shake stack; saturates at 1.
            // 'heavy_trauma' modifier doubles the per-miss add.
            const traumaAdd = this.modifier === "heavy_trauma" ? Game.TRAUMA_MISS_ADD * 2 : Game.TRAUMA_MISS_ADD;
            this.trauma = Math.min(1, this.trauma + traumaAdd);
            // Heavier double-buzz on miss.
            navigator.vibrate?.([20, 30, 40]);
          }
        }
        this.events.push({
          t: Number(eventT.toFixed(3)),
          ring_idx: e.ringIdx,
          passed: e.type === "pass",
        });

        // Achievement-system hook. Track no-miss runtime here so consumers
        // don't need to mirror game timekeeping.
        if (e.type === "miss") this.lastMissAt = t;
        const noMissSeconds = Math.max(0, t - this.lastMissAt);
        const missLateralSigned = e.type === "miss"
          ? e.position.x - this.ship.position.x
          : 0;
        this.cb.onRingEvent?.({
          type: e.type,
          ringIdx: e.ringIdx,
          score: this.score,
          streak: this.streak,
          noMissSeconds,
          gate: isGate,
          boss: isBoss,
          missLateralSigned,
        });
      }

      // Streak ribbon trail. Drops slow particles behind the ship; the world
      // stays still while the ship flies past, producing a visible ribbon.
      // Hue sweeps along the active palette's hueStart→hueEnd as the streak
      // climbs from 5 to 30. Saturation + lightness come from the palette.
      if (this.streak >= 5) {
        const p = this.trailPalette;
        const k = Math.min(1, (this.streak - 5) / 25);
        const h = p.hueStart + k * (p.hueEnd - p.hueStart);
        this.trailColor.setHSL(h, p.sat, p.lum);
        const count = this.streak >= 15 ? 2 : 1;
        this.particles.trail(this.ship.position, this.trailColor, count);
      }

      // Speedrun: end the moment we hit the score target so duration becomes
      // the metric. Checked before time-burn so a target hit on the same
      // frame as time=0 still ends as 'reached'.
      if (this.speedrunTarget > 0 && this.score >= this.speedrunTarget) {
        this.end();
      } else if (this.practice) {
        // Practice mode: clamp at start value, never burn down, never end.
        // Player exits via pause overlay or page reload.
        this.timeLeft = TIME_START;
      } else if (this.endless) {
        // Endless: pin the HUD to the natural 60s cap so the timer reads
        // "stable", never burns, never ends via timeout. Exit is
        // exclusively via endRun() (Pause overlay button). Score remains
        // submittable to the drift-endless scope.
        this.timeLeft = 60;
      } else if (t < this.freezeUntil) {
        // Freeze power-up: skip time burn for the buff window.
      } else {
        this.timeLeft -= dt;
        if (this.timeLeft <= 0) {
          this.timeLeft = 0;
          this.end();
        }
      }
    } else {
      // Outside the running state, decay boost toward 0 so the bed/FOV/blur
      // unwind cleanly at pause and end. Sub-rumble also collapses.
      this.audio.setSpeedIntensity(0);
      this.audio.setApproach(0);
      this.engine.setBoost(0, dt);
    }

    // Trauma decays every frame regardless of state.
    if (this.trauma > 0) {
      this.trauma = Math.max(0, this.trauma - dt * Game.TRAUMA_DECAY);
    }

    this.particles.tick(dt);
    this.vapor.update(dt, this.ship.position);
    this.streakAura.update(dt, this.ship.position, this.state === "running" ? this.streak : 0);
    this.boostRing.update(dt, this.ship.position, this.state === "running" ? this.ship.speedBoost : 0);
    this.updateCamera(dt);
    this.cb.onHud(this.score, this.timeLeft, this.mult);
  }

  private updateBiome() {
    // Anchor on the next ring the player will reach so the palette matches
    // the player's local neighborhood, not the far end of the spawn field.
    const upcoming = this.rings.upcomingIdx;
    if (upcoming === this.lastBiomeIdx) return;
    this.lastBiomeIdx = upcoming;

    const { from, to, t } = biomeFor(upcoming);
    this.biomeBlendA.copy(from.nebula.colorA).lerp(to.nebula.colorA, t);
    this.biomeBlendB.copy(from.nebula.colorB).lerp(to.nebula.colorB, t);
    this.biomeBlendC.copy(from.nebula.colorC).lerp(to.nebula.colorC, t);
    this.biomeFog.copy(from.fog).lerp(to.fog, t);

    this.nebula.setPalette(this.biomeBlendA, this.biomeBlendB, this.biomeBlendC);
    const fog = this.engine.scene.fog as THREE.FogExp2 | null;
    if (fog) fog.color.copy(this.biomeFog);
    this.engine.renderer.setClearColor(this.biomeFog, 1);

    // Modal shift per biome — small enough that the arp still locks to the
    // stem-A pad's root (A2/E3), large enough that consecutive biomes feel
    // tonally distinct. 0 / +2 / +5 / -3 (root / 2nd / 4th / minor-6th).
    const BIOME_SHIFTS = [0, 2, 5, -3];
    const baseIdx = biomeFor(upcoming).index;
    this.audio.setBiomeShift(BIOME_SHIFTS[baseIdx] ?? 0);
  }

  private updateCamera(dt: number) {
    // Hero entrance: at first idle frame, log a start time. For the next 4
    // seconds, blend the desired camera position with an entrance pose so
    // the page opens on a slow cinematic dolly. Skipped under reduced-motion
    // or once the player has started a run (state !== 'idle').
    if (this.entranceStart < 0 && this.state === "idle" && !this.reducedMotionActive()) {
      this.entranceStart = this.engine.clock.elapsedTime;
    }
    let desired = this.ship.position
      .clone()
      .add(new THREE.Vector3(this.ship.group.position.x * 0.05, 1.6, 7));
    if (this.entranceStart >= 0 && this.state === "idle") {
      const e = (this.engine.clock.elapsedTime - this.entranceStart) / Game.ENTRANCE_S;
      if (e < 1) {
        // Smoothstep eased blend from an entrance pose (back, up, slight x
        // sweep) into the resting pose.
        const k = e * e * (3 - 2 * e);
        const entrance = new THREE.Vector3(
          Math.cos(e * Math.PI * 0.5) * 5, // start at +5x, end at 0
          4 + (1 - k) * 1.5,                // start higher, ease down
          11 - k * 4,                        // start back, dolly in
        );
        desired = desired.lerp(entrance, 1 - k);
      }
    }
    this.camPos.lerp(desired, 1 - Math.pow(0.001, dt));
    this.engine.camera.position.copy(this.camPos);

    this.camLook.lerp(
      this.ship.position.clone().add(new THREE.Vector3(0, 0, -8)),
      1 - Math.pow(0.0001, dt),
    );
    this.engine.camera.lookAt(this.camLook);

    // Trauma → camera shake. Apply AFTER lookAt (lookAt overwrites rotation).
    // Suppressed for reduced-motion users so they get zero perturbation.
    if (this.trauma > 0 && !this.reducedMotionActive()) {
      const shake = this.trauma * this.trauma;
      const t = this.engine.clock.elapsedTime;
      // Sin-based pseudo-noise at three uncorrelated frequencies — same role
      // as perlin here (smooth, deterministic, decorrelated), cheaper.
      // Amplitudes bumped from 0.05/0.05/0.08 → 0.07/0.07/0.11 so the trauma²
      // curve still reads with the slower TRAUMA_DECAY; roll dominates so the
      // shake reads as a stumble rather than a uniform jitter.
      const pitch = Math.sin(t * 23.7 + 1.3) * shake * 0.07;
      const yaw   = Math.sin(t * 27.1 + 4.7) * shake * 0.07;
      const roll  = Math.sin(t * 19.9 + 9.2) * shake * 0.11;
      this.engine.camera.rotateX(pitch);
      this.engine.camera.rotateY(yaw);
      this.engine.camera.rotateZ(roll);
    }

    this.nebula.mesh.position.copy(this.engine.camera.position);
  }
}
